import { forwardRef } from 'react';

/**
 * Reusable Form Input — works standalone or with react-hook-form `register()`
 *
 * @param {string}  label       - Field label
 * @param {string}  field       - HTML name / id
 * @param {string}  type        - Input type (text, email, password, number, date …)
 * @param {string}  placeholder
 * @param {string}  error       - Error message string
 * @param {boolean} required
 * @param {boolean} readOnly
 * @param {boolean} disabled
 * @param {React.ReactNode} icon       - Optional leading icon
 * @param {React.ReactNode} rightIcon  - Optional trailing icon / action
 * @param {'sm'|'md'|'lg'} size
 */

const SIZES = {
  sm: 'h-8 text-[12px]',
  md: 'h-[35px] text-[13px]',
  lg: 'h-[44px] text-sm',
};

const FormInput = forwardRef(
  (
    {
      label,
      field,
      type = 'text',
      placeholder,
      error,
      required = false,
      readOnly = false,
      disabled = false,
      icon,
      leftIcon,
      rightIcon,
      size = 'md',
      className = '',
      labelClassName = '',
      inputClassName = '',
      helperText,
      hideError = false,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={`space-y-1.5 ${className}`}>
        {/* Label */}
        {label && (
          <label
            htmlFor={field}
            className={`flex items-center gap-1.5 text-[13px] font-normal text-slate-500 ${labelClassName}`}
          >
            {icon && <span className="text-slate-400 flex-shrink-0">{icon}</span>}
            {label}
            {required && <span className="text-red-500 text-xs">*</span>}
          </label>
        )}

        {/* Input wrapper */}
        <div className="relative">
          <input
            ref={ref}
            id={field}
            name={field}
            type={type}
            readOnly={readOnly}
            disabled={disabled}
            placeholder={placeholder}
            className={[
              'w-full h-[35px] border rounded-md px-3',
              'text-slate-800 placeholder-slate-400',
              'focus:outline-none focus:ring-1 transition-all bg-white leading-none',
              SIZES[size],
              error
                ? 'border-red-300 focus:border-red-500 focus:ring-red-200'
                : 'border-slate-200 focus:border-brand-blue focus:ring-brand-blue/30',
              readOnly && 'bg-slate-50 cursor-not-allowed text-slate-500',
              disabled && 'bg-slate-100 cursor-not-allowed opacity-60',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              inputClassName,
            ]
              .filter(Boolean)
              .join(' ')}
            {...props}
          />
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              {leftIcon}
            </span>
          )}
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {rightIcon}
            </span>
          )}
        </div>

        {/* Error / Helper */}
        {error && (
          <p className="text-[12px] text-red-500 font-medium flex items-center gap-1">
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        )}
        {!error && helperText && (
          <p className="text-[11px] text-slate-400 font-normal">{helperText}</p>
        )}
      </div>
    );
  },
);

FormInput.displayName = 'FormInput';
export default FormInput;
